import React, { useCallback, useEffect, useState } from "react";
import { Search, Loader, Plus, SquareUser } from "lucide-react";
import { numberWithCommas, debounce, can_visit, userMessageTemplates } from "../../helpers/index.js";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { notify, getPractice } from "../../helpers/index.js";
import { deleteBulkFidelisData, deleteFidelisPatient, fetchFidelisdata, fetchFidelisFilterData } from "../../services/fidelisPatient.js";
import DataTablePagination from "../DataTablePagination.jsx";
import Spacing from "../Spacing.jsx";
import FidelisPatientDataTable from "../DataTable/FidelisPatientDataTable.jsx";
import FidelisPatientImportModal from "../Modal/FidelisPatientImportModal.jsx";
import Filter from "../Filter/index.jsx";
import BulkDeleteModal from "../Modal/BulkDeleteModal.jsx";
import CustomBulkMessageSendModal from "../Modal/CustomBulkMessageSendModal.jsx";
import FilterSummary from "../UI/FilterSummary.jsx";

export default function FidelisPatient({ selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit }) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }
  let perPage = 10;

  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    start_date: "",
    end_date: "",
    gender: "",
    visit_status: "",
    practice_location: "",
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [patientFormData, setPatientFormData] = useState(filter);

  const [messageTemplate] = useState(userMessageTemplates());
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [patientDataImport, setPatientDataImport] = useState(false);
  const [importLoader, setImportLoader] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [visitStatusOptions, setVisitStatusOptions] = useState([]);
  const [practiceLocationsOptions, setPracticeLocationsOptions] = useState([]);
  const [fidelisPatientBulkDelete, setFidelisPatientBulkDelete] = useState(false);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchFidelisdata(getFilter);
      if (response) {
        const results = response?.data?.results ?? [];
        setData(results);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadFidelisPatientData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadFilterOptions(account_id, location_id);
  }, [account_id, location_id]);

  const loadFilterOptions = async (accountId, locationId) => {
    try {
      const response = await fetchFidelisFilterData(accountId, locationId);
      if (response) {
        const results = response?.data ?? [];
        
        const visitStatusesOptions = results.visit_statuses?.map((s) => ({ value: s, label: s }));
        setVisitStatusOptions(visitStatusesOptions);
        
        const practiceLocationsOptions = results.practice_locations?.map((s) => ({ value: s, label: s }));
        setPracticeLocationsOptions(practiceLocationsOptions);
      }
    } catch (error) {
      // console.log("🚀 ~ loadFidelisFilterOptions ~ error:", error);
    }
  };

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prevFilter) => ({ ...prevFilter, search_term: value, page: 1 }));
    }, 600), // 600ms debounce delay
    []
  );

  // Handle input change
  const handleChangeSearchTerm = (event) => {
    const value = event.target.value;    
    setDataLoading(true);
    debouncedSearch(value);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);


  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setCheckedIds([]);
    setSelectedPatientData([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleDeleteRecord = async (id) => {
    setDataLoading(true);
    try {
      const response = await deleteFidelisPatient(id, account_id, location_id);

      if (response) {
        if (response?.detail) {
          notify('error', response?.detail);
        } else { 
          if(response?.data?.status === 'success'){
            notify("success", response?.data?.message);
            await loadData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleBulkDeleteRecord = async (e) => {
    setDataLoading(true);
    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_id: checkedIds,
      };

      const response = await deleteBulkFidelisData(deleteData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            await loadData(filter);
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",
    }));
  };

  useDocumentTitle("Fidelis Patient");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24" style={{ marginBottom: "0px" }}>
          <i className="docpro_blue_bg docpro_white_color">
            <SquareUser width={20} height={20} />
          </i>
          Fidelis Patient
          <span>{numberWithCommas(dataCount?.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="5" sm="5" />

      <div className="docpro_data_table_wrap">
        <div className={`docpro_data_table_headings small_heading docpro_grid_gap_10`}>
          <div className={`searching_div docpro_grid_gap_10`}>
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            {can_visit('_bulk_message') && (
              <CustomBulkMessageSendModal
                account_id={account_id}
                location_id={location_id}
                smsLimit={smsLimit}
                setSmsLimit={setSmsLimit}
                selectedPatientData={selectedPatientData}
                setSelectedPatientData={setSelectedPatientData}
                totalPatient={dataCount?.totalCount}
                sendAllPatient={true}
                reloadDataFilter={filter}
                setCheckedIds={setCheckedIds}
                messageTemplate={messageTemplate}
              />
            )}
          </div>

          <div className="docpro_data_table_btns small_filter_btns p-0">
            {(can_visit('fidelis_care') && can_visit('fidelis_care_import')) && (
              <button 
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                onClick={() => {
                  setPatientDataImport(!patientDataImport);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <Plus width={20} height={20} />
                <span>Import</span>
                {importLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}
            <Filter
              filterType="fidelis_care"
              reloadData={setFilter}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              visitStatusOptions={visitStatusOptions}
              practiceLocationsOptions={practiceLocationsOptions}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={filter}
            setFilter={setFilter}
            loadData={loadData}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            showSave={false}
            setDataLoading={setDataLoading}
          />
        </div>
        <FidelisPatientDataTable
          account_id={account_id}
          location_id={location_id}
          tableData={data}
          handleCheckboxChange={handleCheckboxChange}
          handleSelectAll={() => handleSelectAll(data)}
          reloadData={setFilter}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          checkSelected={checkSelected}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          handleDeleteRecord={handleDeleteRecord}
          fidelisPatientBulkDelete={fidelisPatientBulkDelete}
          setFidelisPatientBulkDelete={setFidelisPatientBulkDelete}
          messageTemplate={messageTemplate}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {patientDataImport && (
        <FidelisPatientImportModal
          account_id={account_id}
          location_id={location_id}
          reloadData={loadData}
          reloadDataFilter={filter}
          patientDataImport={patientDataImport}
          setPatientDataImport={setPatientDataImport}
          previousScrollPosition={previousScrollPosition}
          setImportLoader={setImportLoader}
        />
      )}

      {fidelisPatientBulkDelete && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={fidelisPatientBulkDelete}
          setActiveModal={setFidelisPatientBulkDelete}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
